/**
 * MapExtractionContext for managing content extraction in the Needs Map
 * 更新版: APIベースの抽出処理を統合
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { TopicNode, ErrorInfo, ContentItem, updateNodeActiveStatus, Status } from '@/types/nodeTypes';
import { TopicEdge, updateEdgeActiveStatus } from '@/types/edgeTypes';
import { useExtraction } from '@/hooks/useExtraction';
import { toast } from 'sonner';

/**
 * MapExtractionContext type
 */
interface MapExtractionContextType {
  // State
  extracting: boolean;
  progress: number;
  currentlyProcessing: string | null;
  
  // Operations
  processAll: (
    nodes: TopicNode[],
    edges: TopicEdge[],
    transcript: string
  ) => Promise<{ nodes: TopicNode[], edges: TopicEdge[] }>;
  extractNodeContent: (
    node: TopicNode,
    transcript: string
  ) => Promise<TopicNode>;
  extractEdgeContent: (
    edge: TopicEdge,
    sourceNodeActive: boolean,
    targetNodeActive: boolean,
    transcript: string
  ) => Promise<TopicEdge>;
  retryNode: (
    nodeId: string,
    nodes: TopicNode[],
    transcript: string
  ) => Promise<TopicNode | null>;
  retryEdge: (
    edgeId: string,
    nodes: TopicNode[],
    edges: TopicEdge[],
    transcript: string
  ) => Promise<TopicEdge | null>;
}

/**
 * Create MapExtractionContext
 */
const MapExtractionContext = createContext<MapExtractionContextType | undefined>(undefined);

/**
 * MapExtractionProvider props
 */
interface MapExtractionProviderProps {
  children: React.ReactNode;
}

/**
 * MapExtractionProvider component
 */
export const MapExtractionProvider: React.FC<MapExtractionProviderProps> = ({ 
  children 
}) => {
  const {
    extracting,
    progress,
    currentlyProcessing,
    processAll,
    extractNodeContent,
    extractEdgeContent,
    retryNode,
    retryEdge
  } = useExtraction();
  
  const value = {
    extracting,
    progress,
    currentlyProcessing,
    processAll,
    extractNodeContent,
    extractEdgeContent,
    retryNode,
    retryEdge
  };
  
  return (
    <MapExtractionContext.Provider value={value}>
      {children}
    </MapExtractionContext.Provider>
  );
};

/**
 * useMapExtraction hook
 */
export const useMapExtraction = (): MapExtractionContextType => {
  const context = useContext(MapExtractionContext);
  
  if (context === undefined) {
    throw new Error('useMapExtraction must be used within a MapExtractionProvider');
  }
  
  return context;
};